
import { CdkDrag, DragDropModule } from '@angular/cdk/drag-drop';
import { Component, ComponentRef, DestroyRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CalioMatIconComponent } from '@calio-lib/calio-button/calio-mat-icon/calio-mat-icon.component';
import { DynamicDirectiveDirective } from '@calio-lib/calio-directives/dynamic-directive.directive';
import { TranslateModule } from '@ngx-translate/core';
import { CalioEventsService } from '@util-services/calio-events.service';
import { DynamicComponentLoaderService } from '@util-services/dynamic-component-loader.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-calio-dialog',
  templateUrl: './calio-dialog.component.html',
  styleUrls: ['./calio-dialog.component.scss'],
  imports: [TranslateModule, CdkDrag, DragDropModule, CalioMatIconComponent, DynamicDirectiveDirective]
})
export class CalioDialogComponent implements OnInit, OnDestroy {

  @ViewChild(DynamicDirectiveDirective, { static: true }) dynamicDirective: DynamicDirectiveDirective;

  title: string;
  subtitle: string;
  disableDrag = false;
  showDialogCloseButton = true;
  subtitleTranslationJson: any = {};
  customHeaderClass: string;
  loadComponent: () => Promise<any>;
  compData: any;
  headerAction: { labelKey: string, callback: () => void };
  loadDynamicComponentSubscription: Subscription;
  closeCalioDialogEventSubscription: Subscription;
  closeCalioRefDialogEventSubscription: Subscription;

  dialogRef = inject<MatDialogRef<CalioDialogComponent>>(MatDialogRef);
  private dynamicComponentLoaderService = inject(DynamicComponentLoaderService);
  private calioEventsService = inject(CalioEventsService);
  private destroyRef = inject(DestroyRef);
  data = inject<{
    loadComponent: () => Promise<any>,
    compData: any,
    dialogTitle: string,
    dialogSubTitle: string,
    showHeader: boolean,
    dialogSubtitleTranslationJson: any,
    customHeaderClass: string,
    showDialogCloseButton?: boolean,
    disableDrag?: boolean,
    headerAction?: {
      labelKey: string,
      callback: () => void
    }
  }>(MAT_DIALOG_DATA);

  constructor() {
    const data = this.data;

    this.title = data.dialogTitle;
    this.subtitle = data.dialogSubTitle;
    this.customHeaderClass = data.customHeaderClass;
    this.showDialogCloseButton = data.showDialogCloseButton === false ? false : true;
    this.disableDrag = !!data.disableDrag;
    this.loadComponent = data.loadComponent;
    this.compData = data.compData;
    this.headerAction = data.headerAction;
    if (data.dialogSubtitleTranslationJson) {
      this.subtitleTranslationJson = data.dialogSubtitleTranslationJson;
    }

    this.closeCalioDialogEventSubscription = this.calioEventsService.closeCalioDialogEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: any) => {
        this.dialogRef.close(result);
      });

    this.closeCalioRefDialogEventSubscription = this.calioEventsService.closeCalioRefDialogEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((
        result: { ref: any, result?: any }
      ) => {
        result.ref.close(result?.result);
      });

    this.calioEventsService.calioDialogTitleChangedEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: {
        title: string,
        subtitle: string
      }) => {
        this.title = result.title;
        this.subtitle = result.subtitle;
      });
  }

  ngOnInit(): void {
    const viewContainerRef = this.dynamicDirective.viewContainerRef;
    this.loadDynamicComponentSubscription = this.dynamicComponentLoaderService.loadComponentDynamically(
      viewContainerRef,
      this.loadComponent
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: ComponentRef<any>) => {
          this.compData.dialogRef = this.dialogRef;
          result.instance.compData = this.compData;
        }
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.loadDynamicComponentSubscription?.unsubscribe();
    this.closeCalioDialogEventSubscription?.unsubscribe();
    this.closeCalioRefDialogEventSubscription?.unsubscribe();
  }
}
